/**
 * Source type configuration with descriptions
 */
export interface SourceTypeConfig {
  name: string;
  description: string;
  emoji: string;
  examples?: string; // Shown on hover in lower left
  category: 'Products' | 'Marketing' | 'Creative/Art Direction' | 'Production';
}

/**
 * Common source types available for selection during onboarding
 */
export const COMMON_SOURCE_TYPES: SourceTypeConfig[] = [
  // Products
  {
    name: 'Products',
    description: 'Track items that drive file naming and organization',
    emoji: '📦',
    examples: 'ie: SKUs, product lines, collections',
    category: 'Products',
  },
  {
    name: 'Product Samples',
    description: 'On-set versions for sizing and fit',
    emoji: '🎁',
    examples: 'ie: Sample sizes, fit models, test items',
    category: 'Products',
  },
  {
    name: 'Look / Product Groupings',
    description: 'Collections of products for specific looks',
    emoji: '🏷️',
    category: 'Products',
  },

  // Marketing
  {
    name: 'Marketing Story',
    description: 'Campaign themes or seasonal moments',
    emoji: '📖',
    examples: 'ie: Holiday stories, brand narratives',
    category: 'Marketing',
  },
  {
    name: 'Marketing Campaign',
    description: 'Campaign names or initiatives',
    emoji: '📢',
    category: 'Marketing',
  },
  {
    name: 'Marketing Spot',
    description: 'Specific ad slots or channels',
    emoji: '📱',
    examples: 'ie: Instagram Stories, TV commercial, homepage banner',
    category: 'Marketing',
  },
  {
    name: 'Marketing Placements',
    description: 'Strategic product placement in content',
    emoji: '🎯',
    examples: 'ie: In-content integration, native ads',
    category: 'Marketing',
  },
  {
    name: 'Season',
    description: 'Seasonal collections or periods',
    emoji: '🗓️',
    examples: 'ie: Summer, Fall, Winter, Spring',
    category: 'Marketing',
  },

  // Creative/Art Direction
  {
    name: 'Creative Team',
    description: 'Production and creative staff',
    emoji: '👨‍🎨',
    examples: 'ie: Photographers, designers, art directors',
    category: 'Creative/Art Direction',
  },
  {
    name: 'Shot List / Angles',
    description: 'Required shots or compositions',
    emoji: '📷',
    category: 'Creative/Art Direction',
  },
  {
    name: 'Props & Accessories',
    description: 'Physical items needed for shoots',
    emoji: '✨',
    examples: 'ie: Props, accessories, set pieces',
    category: 'Creative/Art Direction',
  },
  {
    name: 'Hair & Makeup References',
    description: 'Beauty references and inspiration',
    emoji: '💄',
    category: 'Creative/Art Direction',
  },
  {
    name: 'Swipe',
    description: 'Inspiration and creative direction references',
    emoji: '💡',
    examples: 'ie: Mood boards, art direction, inspiration',
    category: 'Creative/Art Direction',
  },
  {
    name: 'Styleout Images',
    description: 'How specific products should be combined',
    emoji: '👗',
    examples: 'ie: Outfit combinations, product pairings',
    category: 'Creative/Art Direction',
  },

  // Production (Logistical)
  {
    name: 'Talent',
    description: 'On-camera and promotional partners',
    emoji: '👥',
    examples: 'ie: Models, influencers, UGC creators, affiliates',
    category: 'Production',
  },
  {
    name: 'Locations',
    description: 'Shoot locations or studios',
    emoji: '📍',
    examples: 'ie: Studio A, outdoor locations',
    category: 'Production',
  },
  {
    name: 'Set Type',
    description: 'Type of content being captured',
    emoji: '🎬',
    examples: 'ie: Photo, video, hybrid',
    category: 'Production',
  },
  {
    name: 'Delivery Format',
    description: 'Output formats for final cards',
    emoji: '📄',
    examples: 'ie: Photo, video, animation, GIF',
    category: 'Production',
  },
  {
    name: 'Card Delivery Date',
    description: 'When cards are due',
    emoji: '📅',
    category: 'Production',
  },
  {
    name: 'Card Launch Date',
    description: 'When cards go live',
    emoji: '🚀',
    category: 'Production',
  },
  {
    name: 'Production / Post Notes',
    description: 'Production requirements and post-production specs',
    emoji: '🖌️',
    examples: 'ie: Retouching levels, edit notes',
    category: 'Production',
  },
  {
    name: 'Guides / Standards',
    description: 'Brand guidelines or technical specs',
    emoji: '📋',
    category: 'Production',
  },
  {
    name: 'Usage Rights',
    description: 'Licensing terms or permissions',
    emoji: '⚖️',
    category: 'Production',
  },
];

/**
 * Project type configuration with emoji and description
 */
export interface ProjectTypeConfig {
  name: string;
  emoji: string;
  description: string;
}

/**
 * Common project types available during onboarding with emojis
 */
export const COMMON_PROJECT_TYPES: ProjectTypeConfig[] = [
  {
    name: 'Photo shoot',
    emoji: '📸',
    description: 'Product photography, lifestyle, or studio shoots',
  },
  { name: 'Marketing campaign', emoji: '📢', description: 'Multi-channel marketing initiatives' },
  { name: 'Card Request', emoji: '🎨', description: 'Creative card production and delivery' },
  { name: 'Styleout', emoji: '👗', description: 'Fashion styling and product curation' },
  {
    name: 'Design / retouching request',
    emoji: '✨',
    description: 'Image editing and design work',
  },
  { name: 'Mood Board', emoji: '🎨', description: 'Visual inspiration and creative direction' },
];

/**
 * Mapping of project types to suggested source types
 * These are pre-selected but users can modify the selection
 */
export const PROJECT_TYPE_TO_SOURCES: Record<string, string[]> = {
  'Photo shoot': [
    'Products',
    'Product Samples',
    'Talent',
    'Creative Team',
    'Locations',
    'Set Type',
    'Shot List / Angles',
    'Props & Accessories',
  ],
  'Marketing campaign': [
    'Products',
    'Marketing Story',
    'Marketing Spot',
    'Marketing Campaign',
    'Season',
    'Marketing Placements',
    'Card Launch Date',
  ],
  'Card Request': ['Products', 'Delivery Format', 'Card Delivery Date', 'Usage Rights'],
  Styleout: [
    'Products',
    'Product Samples',
    'Talent',
    'Styleout Images',
    'Season',
    'Look / Product Groupings',
  ],
  'Design / retouching request': [
    'Products',
    'Delivery Format',
    'Production / Post Notes',
    'Card Delivery Date',
  ],
  'Mood Board': ['Swipe', 'Marketing Story', 'Season', 'Hair & Makeup References'],
};

/**
 * Get description for a source type
 */
export function getSourceTypeDescription(sourceName: string): string | undefined {
  const config = COMMON_SOURCE_TYPES.find((s) => s.name === sourceName);
  return config?.description;
}

/**
 * Get suggested sources for a given project type
 * @param projectType - The project type to get suggestions for
 * @returns Array of suggested source type names
 */
export function getSuggestedSourcesForProjectType(projectType: string): string[] {
  return PROJECT_TYPE_TO_SOURCES[projectType] || [];
}

/**
 * Check if a project type matches a preset name exactly (case-insensitive)
 * @param projectType - The project type to check
 * @returns The matching preset name or null
 */
export function getMatchingPresetProjectType(projectType: string): string | null {
  const normalized = projectType.trim();
  const match = COMMON_PROJECT_TYPES.find(
    (preset) => preset.name.toLowerCase() === normalized.toLowerCase(),
  );
  return match?.name || null;
}

/**
 * Get category for a source type
 * @param sourceName - The source name to look up
 * @returns The category or undefined if not found
 */
export function getSourceCategory(sourceName: string): string | undefined {
  const config = COMMON_SOURCE_TYPES.find((s) => s.name === sourceName);
  return config?.category;
}
